import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion, useAnimationControls } from 'framer-motion';
import type { ExperienceEntry } from '../../Model/resume';
import styles from './ResumeRow.module.css';

const BLOCK_DURATION = 0.2;

function isRoughlyOnScreen(el: HTMLElement | null): boolean {
    if (!el) return false;
    const rect = el.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    const vw = window.innerWidth || document.documentElement.clientWidth;
    return rect.bottom > 0 && rect.top < vh && rect.right > 0 && rect.left < vw;
}

function formatDateRange(entry: ExperienceEntry): string {
    return `${entry.startDate} – ${entry.endDate}`;
}

export interface ResumeRowProps {
    entry: ExperienceEntry;
    transitionId: string;
    /** Milliseconds to wait before starting the paint-in (top row typically 0). */
    staggerInMs?: number;
    phase: 'in' | 'out';
    transitionToken: number;
    onHideComplete: (id: string, token: number) => void;
}

const ResumeRow: React.FC<ResumeRowProps> = ({
    entry,
    transitionId,
    staggerInMs = 0,
    phase,
    transitionToken,
    onHideComplete,
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const blockerControls = useAnimationControls();
    const [textOpacity, setTextOpacity] = useState(0);
    const [expanded, setExpanded] = useState(false);
    const cancelPaintRef = useRef(false);
    const completedHideTokenRef = useRef<number | null>(null);

    const toggleExpanded = useCallback(() => {
        setExpanded((value) => !value);
    }, []);

    const onPanelKeyDown = useCallback(
        (event: React.KeyboardEvent) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            toggleExpanded();
        },
        [toggleExpanded],
    );

    const runRevealPaint = useCallback(
        async (shouldWipe: boolean) => {
            cancelPaintRef.current = false;
            await new Promise<void>((resolve) => setTimeout(resolve, staggerInMs));
            if (cancelPaintRef.current) return;

            if (!shouldWipe) {
                blockerControls.set({ scaleX: 0, transformOrigin: 'left center' });
                setTextOpacity(1);
                return;
            }

            await blockerControls.start({
                scaleX: 1,
                transformOrigin: 'left center',
                transition: { duration: BLOCK_DURATION, ease: 'easeInOut' },
            });
            if (cancelPaintRef.current) return;
            setTextOpacity(1);
            await blockerControls.start({
                scaleX: 0,
                transformOrigin: 'right center',
                transition: { duration: BLOCK_DURATION, ease: 'easeInOut' },
            });
        },
        [blockerControls, staggerInMs],
    );

    const runHidePaint = useCallback(
        async (shouldWipe: boolean, token: number) => {
            cancelPaintRef.current = false;

            if (!shouldWipe) {
                setTextOpacity(0);
                onHideComplete(transitionId, token);
                return;
            }

            blockerControls.set({ scaleX: 0, transformOrigin: 'left center' });
            await blockerControls.start({
                scaleX: 1,
                transformOrigin: 'left center',
                transition: { duration: BLOCK_DURATION, ease: 'easeInOut' },
            });
            if (cancelPaintRef.current) return;
            setTextOpacity(0);
            await blockerControls.start({
                scaleX: 0,
                transformOrigin: 'right center',
                transition: { duration: BLOCK_DURATION, ease: 'easeInOut' },
            });
            if (!cancelPaintRef.current) {
                onHideComplete(transitionId, token);
            }
        },
        [blockerControls, onHideComplete, transitionId],
    );

    useEffect(() => {
        if (phase !== 'in') return;

        cancelPaintRef.current = false;
        const shouldWipe = isRoughlyOnScreen(containerRef.current);
        void runRevealPaint(shouldWipe);

        return () => {
            cancelPaintRef.current = true;
            blockerControls.stop();
        };
    }, [blockerControls, phase, runRevealPaint]);

    useEffect(() => {
        if (phase !== 'out') return;

        if (completedHideTokenRef.current === transitionToken) return;
        completedHideTokenRef.current = transitionToken;

        cancelPaintRef.current = false;
        const shouldWipe = isRoughlyOnScreen(containerRef.current);
        void runHidePaint(shouldWipe, transitionToken);

        return () => {
            cancelPaintRef.current = true;
            blockerControls.stop();
        };
    }, [blockerControls, phase, runHidePaint, transitionToken]);

    return (
        <div ref={containerRef} className={styles.wrap}>
            <motion.div
                className={styles.panel}
                initial={{ opacity: 0 }}
                animate={{ opacity: textOpacity }}
                transition={{ duration: 0 }}
                role="button"
                tabIndex={0}
                aria-expanded={expanded}
                aria-label={`${expanded ? 'Hide' : 'Show'} accomplishments for ${entry.company}`}
                onClick={toggleExpanded}
                onKeyDown={onPanelKeyDown}
            >
                <div className={styles.headerRow}>
                    <div className={styles.main}>
                        <div className={styles.company}>{entry.company}</div>
                        <div className={styles.role}>{entry.role}</div>
                    </div>
                    <div className={styles.meta}>
                        <div className={styles.dates}>{formatDateRange(entry)}</div>
                        <span
                            className={`${styles.chevron}${expanded ? ` ${styles.chevronOpen}` : ''}`}
                            aria-hidden
                        />
                    </div>
                </div>
                <div
                    className={`${styles.expandGrid}${expanded ? ` ${styles.expandGridOpen}` : ''}`}
                    aria-hidden={!expanded}
                >
                    <div className={styles.expandInner}>
                        <ul className={styles.bullets}>
                            {entry.accomplishments.map((item, accomplishmentIndex) => (
                                <li key={accomplishmentIndex} className={styles.bullet}>
                                    {item}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </motion.div>
            <motion.div
                className={styles.blocker}
                aria-hidden
                initial={{ scaleX: 0 }}
                animate={blockerControls}
                style={{ transformOrigin: 'left center' }}
            />
        </div>
    );
};

export default ResumeRow;
